import React from 'react';
import { FindAllCompaniesResponse } from '@ansvar-sakerhet/api-client';
import { useTable } from 'react-table';
import { S } from './SubcontractorsTable.style';
import { useCompaniesTableData } from './hooks/useCompaniesTableData';
import * as Dialog from '@radix-ui/react-dialog';
import { CreateBGControl } from './components/CreateBGControl/CreateBGControl';
import { useDeleteCompany } from '../../../../../../../../../../traits/api/companies/deleteCompany';
import { useUpdateCompany } from '../../../../../../../../../../traits/api/companies/useUpdateCompany';
import { Input } from '../../../../../../../../../../components/Input';
import { useAuth } from '../../../../../../../../../../traits/hooks/useAuth';
import { ConfirmDeleteDialog } from '../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { BackgroundControlFilesTable } from '../../../../components/BackgroundControlFilesTable/BackgroundControlFilesTable';
import {
  countCompanyFiles,
  findCompanyBackgroundControls,
} from './utils/companyBackgroundControls';
import { CompaniesSortControls } from '../../../../../../../../../../traits/api/companies/useGetAllCompanies';

export const SubcontractorsTable = ({
  companies,
  clientId,
  sort,
  setSort,
}: {
  companies: FindAllCompaniesResponse[];
  clientId: string;
  sort: CompaniesSortControls | undefined;
  setSort: (sort: CompaniesSortControls) => void;
}) => {
  const { isSafetyCoordinator, isSuperAdmin } = useAuth();
  const canDeleteCompany = isSafetyCoordinator || isSuperAdmin;
  const [companyToDelete, setCompanyToDelete] =
    React.useState<FindAllCompaniesResponse | null>(null);
  const data = useCompaniesTableData(
    companies,
    canDeleteCompany ? { onDeleteCompany: setCompanyToDelete } : undefined
  );
  const table = useTable({
    ...data,
    getRowId: (row) => row.companyId,
  });
  const [open, setOpen] = React.useState<null | string>();
  const [addOpen, setAddOpen] = React.useState(false);
  const [projectId, setProjectId] = React.useState<null | string>(null);
  const [companyReg, setCompanyReg] = React.useState<null | string>(null);
  const [renameName, setRenameName] = React.useState('');
  const updateCompany = useUpdateCompany();

  const deleteComp = useDeleteCompany({
    onDeleteFileSuccess: () => setCompanyToDelete(null),
  });

  const selectedCompany =
    open !== undefined && open !== null
      ? companies.find((company) => company.companyId === open)
      : undefined;

  React.useEffect(() => {
    setRenameName(selectedCompany?.name ?? '');
  }, [selectedCompany?.companyId, selectedCompany?.name]);

  const renameCompany = async () => {
    if (!selectedCompany || !renameName.trim()) return;
    await updateCompany.mutateAsync({
      companyId: selectedCompany.companyId,
      data: { name: renameName.trim() },
    });
  };

  return (
    <>
      <S.Button onClick={() => setAddOpen(true)}>
        <S.Icon src="Plus" /> Skapa bakgrundskontroll
      </S.Button>
      <CreateBGControl
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        clientId={clientId}
        projectId={projectId}
        companyReg={companyReg}
      />

      <ConfirmDeleteDialog
        isOpen={companyToDelete !== null}
        title={`Ta bort ${companyToDelete?.name ?? 'bolaget'}?`}
        description={`Bolaget och ${
          companyToDelete ? countCompanyFiles(companyToDelete) : 0
        } tillhörande filer raderas. Detta kan inte ångras.`}
        isLoading={deleteComp.isLoading}
        onClose={() => setCompanyToDelete(null)}
        onConfirm={() => {
          if (!companyToDelete) return;
          deleteComp.mutate({ companyId: companyToDelete.companyId });
        }}
      />
      <S.TableContainer>
        <S.AltTable {...table.getTableProps()}>
          <S.AltTHead>
            {table.headerGroups.map((headerGroup) => (
              <S.AltTHeadRow
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroup.getHeaderGroupProps().key}
              >
                {headerGroup.headers.map((column) => {
                  const headerData = data.columns.filter(
                    ({ accessor }) => accessor === column.id
                  )[0];
                  const interactiveProps = headerData?.onClick
                    ? { onClick: () => headerData.onClick(setSort, sort) }
                    : {};
                  const iconSrc = headerData?.iconSrc?.(sort);

                  return (
                    <S.AltTHeadCell
                      {...column.getHeaderProps()}
                      {...interactiveProps}
                      key={column.getHeaderProps().key}
                    >
                      <S.TableText>{column.render('Header')}</S.TableText>
                      {iconSrc && <S.CenteredIcon src={iconSrc} />}
                    </S.AltTHeadCell>
                  );
                })}
              </S.AltTHeadRow>
            ))}
          </S.AltTHead>

          <S.AltTBody {...table.getTableBodyProps()}>
            {table.rows.map((row) => {
              table.prepareRow(row);

              return (
                <>
                  <S.AltTBodyRow
                    {...row.getRowProps()}
                    key={row.getRowProps().key}
                  >
                    {row.cells.map((cell) => {
                      if (cell.column.id === 'name') {
                        return (
                          <>
                            <S.AltTBodyCell
                              onClick={() => {
                                if (open === row.id) {
                                  setOpen(null);
                                } else {
                                  setOpen(row.id);
                                }
                              }}
                              {...cell.getCellProps()}
                              key={cell.getCellProps().key}
                            >
                              {cell.render('Cell')}
                            </S.AltTBodyCell>
                          </>
                        );
                      }
                      return (
                        <S.AltTBodyCell
                          {...cell.getCellProps()}
                          key={cell.getCellProps().key}
                        >
                          {cell.render('Cell')}
                        </S.AltTBodyCell>
                      );
                    })}
                  </S.AltTBodyRow>
                </>
              );
            })}
          </S.AltTBody>
        </S.AltTable>
      </S.TableContainer>
      <Dialog.Root open={open !== undefined && open !== null}>
        <Dialog.Portal>
          <S.Overlay>
            <S.Content>
              <S.ContentHeader>
                <Dialog.Title asChild>
                  <S.H4>Tidigare bakgrundskontroller</S.H4>
                </Dialog.Title>

                <S.CloseButton onClick={() => setOpen(null)}>
                  <S.Icon src="Close" width={20} height={20} />
                </S.CloseButton>
              </S.ContentHeader>
              <BackgroundControlFilesTable
                controls={
                  selectedCompany
                    ? findCompanyBackgroundControls(selectedCompany)
                    : []
                }
              />

              <S.Margin $vertical="xl" />
              <Input
                label="Byt namn"
                value={renameName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRenameName(e.target.value)
                }
              />
              <S.Margin $vertical="s" />
              <S.Button onClick={() => renameCompany()}>Spara namn</S.Button>
              <S.Margin $vertical="s" />
              <S.Button
                onClick={() => {
                  if (!selectedCompany) return;
                  setProjectId(
                    selectedCompany.scopeProjectId ?? selectedCompany.projectId
                  );
                  setCompanyReg(selectedCompany.registrationNumber);
                  setOpen(null);
                  setAddOpen(true);
                }}
              >
                Skapa bakgrundskontroll
              </S.Button>
            </S.Content>
          </S.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};
